import { useEffect, useId, useRef, type HTMLAttributes, type MouseEvent, type ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'
import { cx } from './cx'
import s from './ErrorSummary.module.css'

export interface ErrorSummaryItem {
  /** The `id` of the control the error is about — the link's target. */
  field: string
  /** The field's own label, as the form shows it ("Legal Name"). */
  label: ReactNode
  /** What is wrong, finishing the sentence the label starts ("must match the
   *  company register exactly"). */
  message: ReactNode
}

export interface ErrorSummaryProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** The one line that says what stopped the save: "Two fields need attention
   *  before this record can be saved". Worded by the host. */
  title: ReactNode
  errors: readonly ErrorSummaryItem[]
  /**
   * Move focus to the summary when it appears. A form's submit handler wants
   * this: the person pressed Save, nothing was saved, and the keyboard should
   * land on the reason rather than on a button that did nothing.
   */
  focusOnShow?: boolean
}

/**
 * THE FORM-LEVEL ERROR SUMMARY (#2406, "Input Controls and Validation": "form
 * errors gather at the top of the slab with links").
 *
 * Three levels of validation that never repeat each other: FIELD errors under
 * the field (`Field`), SERVER checks on the row that triggered them
 * (`ServerCheck`), and this — the list at the top, one line per field, each a
 * link that takes the person TO the field.
 *
 * ── what makes it more than a red box ───────────────────────────────────────
 *   · It is ANNOUNCED: `role="alert"` when it appears, so a screen-reader user
 *     who pressed Save hears why nothing happened.
 *   · Each link is a real `<a href="#id">` — it works with the keyboard, reads
 *     as a link, and survives a script failure by scrolling to the field — and
 *     it also FOCUSES the control, which a bare fragment does not do for an
 *     input.
 *   · With no errors it renders NOTHING: an empty summary is a red box that
 *     says nothing, which is worse than no box.
 * The glyph and the 3px crimson cut carry the state beside the words — never
 * colour alone.
 */
export function ErrorSummary({ title, errors, focusOnShow, className, ...rest }: ErrorSummaryProps) {
  const titleId = useId()
  const box = useRef<HTMLDivElement>(null)
  const shown = errors.length > 0

  useEffect(() => {
    if (shown && focusOnShow) box.current?.focus()
  }, [shown, focusOnShow])

  if (!shown) return null

  const go = (event: MouseEvent<HTMLAnchorElement>, field: string) => {
    const target = event.currentTarget.ownerDocument.getElementById(field)
    if (!target) return
    event.preventDefault()
    target.scrollIntoView?.({ block: 'center' })
    target.focus()
  }

  return (
    <div
      ref={box}
      className={cx(s.summary, className)}
      role="alert"
      aria-labelledby={titleId}
      tabIndex={-1}
      data-error-summary=""
      {...rest}
    >
      <AlertCircle className={s.glyph} aria-hidden />
      <div className={s.body}>
        <b className={s.title} id={titleId}>{title}</b>
        <ul className={s.list}>
          {errors.map((error) => (
            <li key={error.field}>
              <a href={`#${error.field}`} className={s.link} onClick={(event) => go(event, error.field)}>
                {error.label}
              </a>{' '}
              {error.message}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
